<!-- src/layouts/MainLayout.vue -->
<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import MenuLeft from '@/components/MenuLeft.vue'
import AppLoader from '@/components/AppLoader.vue'
import { webAlert } from '@/utils/dialog'
import apiClient from '@/api'

const router = useRouter()
const saving = ref(false)
const savingTXT = ref('')

const username = ref(
    localStorage.getItem('user_email') ||
    'Użytkownik'
)
const usertype = ref(
    (localStorage.getItem('user_role') === 'admin_user' ? 'Administrator' : 'Użytkownik')
)

const isCollapsed = ref(false)

const handleLogout = async () => {

    //let response;
    saving.value = true
    savingTXT.value = 'Zamykanie sesji'
    try {
        await apiClient.post({ action: 'logout' })

        localStorage.removeItem('integrator_token')
        localStorage.removeItem('user_email')
        localStorage.removeItem('user_role')
        localStorage.removeItem('admin_email')

    } catch (error) {
        webAlert('Błąd podczas ładowania danych z serwera.')
    } finally {
        saving.value = false
    }

    router.push('/admin/login')
}
const isDark = ref(false);

const applyTheme = (dark) => {
    isDark.value = dark;
    // Zapamiętanie w localStorage
    localStorage.setItem('theme', dark ? 'dark' : 'light');

    // Nałożenie klasy lub atrybutu (jeśli używasz Bootstrap 5.3+)
    if (dark) {
        document.documentElement.setAttribute('data-bs-theme', 'dark');
    } else {
        document.documentElement.setAttribute('data-bs-theme', 'light');
    }
};

const toggleTheme = () => {
    applyTheme(!isDark.value);
};

// Inicjalizacja przy starcie
onMounted(() => {
    const savedTheme = localStorage.getItem('theme');

    if (savedTheme) {
        applyTheme(savedTheme === 'dark');
    } else {
        // Opcjonalnie: sprawdzenie preferencji systemu operacyjnego
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        applyTheme(prefersDark);
    }

});
</script>

<template>
    <div class="app-layout d-flex min-vh-100 bg-light">

        <!-- 1. SIDEBAR DLA DUŻYCH EKRANÓW (DESKTOP) -->
        <aside class="sidebar d-none d-md-flex bg-menu border-end flex-column justify-content-between pt-3 pb-3"
            :class="{ 'is-collapsed': isCollapsed }">
            <div>
                <div class="text-center align-items-center fw-bold gap-2 text-decoration-none px-2 logo-wrapper"
                    style="color: rgb(255, 87, 88) !important;">
                    <span class="fs-5 brand-text">Xmotify</span>
                    <span class="fs-5 brand-icon">X</span>
                </div>

                <hr class="text-secondary opacity-25 my-3" />

                <nav class="nav nav-pills flex-column">
                    <MenuLeft />
                </nav>
            </div>

            <div class="user-footer pt-3 ps-2 border-top">
                <div class="d-flex align-items-center justify-content-between">
                    <div class="d-flex flex-column overflow-hidden me-2 user-logged">
                        <span class="fw-semibold text-white text-truncate small">{{ username }}</span>
                        <span class="text-white extra-small">{{ usertype }}</span>
                    </div>
                    <div class="d-flex align-items-center flex-nowrap action-buttons">
                    <button @click="isCollapsed = !isCollapsed"
                        class="btn btn-sm btn-outline-secondary border-0 p-2 me-1"
                        :title="isCollapsed ? 'Rozwiń menu' : 'Zwiń menu'">
                        <i class="bi" :class="isCollapsed ? 'bi-chevron-double-right' : 'bi-chevron-double-left'"></i>
                    </button>
                    <button @click="handleLogout" class="btn btn-sm btn-outline-danger border-0 p-2"
                        title="Wyloguj się">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="2">
                            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                            <polyline points="16 17 21 12 16 7"></polyline>
                            <line x1="21" y1="12" x2="9" y2="12"></line>
                        </svg>
                    </button>
                    </div>
                </div>
            </div>
        </aside>

        <!-- 2. MENU WYSYWANE DLA MOBILES (BOOTSTRAP OFFCANVAS) -->
        <div class="offcanvas offcanvas-start" tabindex="-1" id="mobileSidebar" aria-labelledby="mobileSidebarLabel">
            <div class="offcanvas-header border-bottom">
                <h5 class="offcanvas-title fw-bold text-primary d-flex align-items-center gap-2"
                    id="mobileSidebarLabel">
                    Xmotify
                </h5>
                <button type="button" class="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
            </div>

            <div class="offcanvas-body d-flex flex-column justify-content-between">
                <nav class="nav nav-pills flex-column">
                    <MenuLeft :is-mobile="true" />
                </nav>
                <div class="pt-3 border-top">
                    <div class="d-flex align-items-center justify-content-between mb-2">
                        <span class="fw-semibold text-white small">{{ username }}</span>
                        <span class="badge bg-secondary">{{ usertype }}</span>
                    </div>
                    <button @click="handleLogout" class="btn btn-outline-danger w-100 btn-sm"
                        data-bs-dismiss="offcanvas">
                        Wyloguj się
                    </button>
                </div>
            </div>
        </div>

        <!-- 3. PRAWA STRONA (GLÓWNA TREŚĆ) -->
        <div class="main-wrapper flex-grow-1 d-flex flex-column min-vh-100">

            <!-- NAGŁÓWEK MOBILNY Z GUZIKIEM HAMBURGERA -->
            <header class="d-md-none  border-bottom p-3 d-flex align-items-center justify-content-between sticky-top">
                <button class="btn btn-outline-secondary border shadow-sm p-2" type="button" data-bs-toggle="offcanvas"
                    data-bs-target="#mobileSidebar" aria-controls="mobileSidebar">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="3" y1="12" x2="21" y2="12"></line>
                        <line x1="3" y1="6" x2="21" y2="6"></line>
                        <line x1="3" y1="18" x2="21" y2="18"></line>
                    </svg>
                </button>

                <span class="fw-bold text-primary">Integrator</span>

                <button @click="handleLogout" class="btn btn-sm btn-outline-danger">
                    Wyloguj
                </button>
            </header>

            <!-- GŁÓWNA TREŚĆ -->
            <main class="flex-grow-1 px-0 py-3 py-md-4 px-md-0">
                <div class="container-fluid max-width-container">
                    <router-view />
                </div>
            </main>

            <!-- STOPKA -->
            <footer class=" border-top py-2 px-3 px-md-4">
                <div
                    class="container-fluid d-flex flex-column flex-sm-row justify-content-between align-items-center gap-1 small text-muted">
                    <div>
                        Status API: <span
                            class="badge bg-success-subtle text-success border border-success-subtle ms-1">Online</span>
                    </div>
                    <button @click="toggleTheme"
                        class="btn btn-sm btn-outline-secondary d-flex align-items-center gap-2" type="button">
                        <i class="bi" :class="isDark ? 'bi-sun-fill text-warning' : 'bi-moon-stars-fill'"></i>
                        <span>{{ isDark ? 'Jasny' : 'Ciemny' }}</span>
                    </button>
                    <div>
                        Wersja systemu: <strong class="text-muted">1.0.0</strong>
                    </div>
                </div>
            </footer>

        </div>

    </div>
    <AppLoader :show="false" text="Pobieranie danych, proszę czekać..." />

</template>

<style scoped>
footer {
    background-color: var(--footer-bg);
}

header {
    background-color: var(--header-background-color);
}

/* SZEROKOŚĆ I ZACHOWANIE SIDEBARU DESKTOP */
.sidebar {
    width: 260px;
    min-width: 260px;
    position: sticky;
    top: 0;
    height: 100vh;
    z-index: 100;
}

/* STYLIZACJA LINKÓW NAV */
.nav-link {
    color: #475569;
    font-weight: 500;
    padding: 10px 14px;
    border-radius: 8px;
    transition: all 0.15s ease-in-out;
}

.nav-link:hover {
    color: #0d6efd;
    background-color: #f8fafc;
}

.nav-link.router-link-active {
    color: #0d6efd !important;
    background-color: #e7f1ff;
    font-weight: 600;
}

.extra-small {
    font-size: 0.75rem;
}

.max-width-container {
    max-width: 100%;
    margin: 0 auto;
}

.bg-menu {
    --bs-bg-opacity: 1;
    background-color: var(--left-menu-bg) !important;
}

.offcanvas {
    background-color: var(--left-menu-bg);
    max-width: 90% !important;
}

@media (min-width:768px) {
    main {
        max-width: calc(100vw - 260px);
        overflow: hidden;
    }

    aside.sidebar.is-collapsed ~ div main {
        max-width: calc(100vw - 70px);
    }
}

.sidebar {
  width: 260px;
  min-width: 260px;
  height: 100vh;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow-x: hidden;
}

.brand-icon {
  display: none;
}

.nav-link {
  white-space: nowrap;
  padding: 0.6rem 0.8rem;
}

.nav-link i {
  font-size: 1.2rem;
  min-width: 24px;
  text-align: center;
}

/* Stan Zzwinięty (Is Collapsed) */
.sidebar.is-collapsed {
  width: 70px;
  min-width: 70px;
}

.sidebar.is-collapsed .brand-text,
.sidebar.is-collapsed .link-text,
.sidebar.is-collapsed .user-logged,
.sidebar.is-collapsed .user-info {
  display: none !important;
}

.sidebar.is-collapsed .brand-icon {
  display: inline-block;
}

.sidebar.is-collapsed .nav-link {
  justify-content: center;
  padding-left: 0;
  padding-right: 0;
}

.sidebar.is-collapsed .user-footer .d-flex {
  justify-content: center !important;
  flex-direction: column-reverse;
  gap: 0.5rem;
}

aside nav {
    max-height: calc(100vh - 155px);
    overflow-y:auto;
}

.action-buttons {
  display: flex !important;
  flex-direction: row !important;
  flex-wrap: nowrap !important;
  flex-shrink: 0 !important;
}
</style>